<template>
  <div class="message-settings-container">
    <div class="settings-header">
      <div class="header-icon mdi mdi-cog-outline"></div>
      <div class="header-content">
        <h3>Delivery Rules</h3>
        <p>Choose who receives automated WhatsApp report notifications.</p>
      </div>
    </div>

    <div class="settings-content">
      <!-- Main Toggle Section -->
      <div class="main-toggle-section">
        <div class="main-toggle-card">
          <div class="main-toggle-info">
            <div class="main-toggle-title">
              <span class="main-toggle-icon mdi mdi-whatsapp"></span>
              WhatsApp Delivery
            </div>
            <div class="main-toggle-description">
              Turns automated WhatsApp messaging on or off for this workspace.
            </div>
          </div>
          <div class="main-toggle-control">
            <div class="main-toggle-switch">
              <input
                type="checkbox"
                v-model="mainToggleEnabled"
                @change="handleMainToggleChange"
                :disabled="loading"
                class="main-toggle-input"
                id="mainToggle"
              />
              <label for="mainToggle" class="main-toggle-slider"></label>
            </div>
            <span
              class="main-toggle-status"
              :class="{
                enabled: mainToggleEnabled,
                disabled: !mainToggleEnabled
              }"
            >
              {{ mainToggleEnabled ? "Enabled" : "Disabled" }}
            </span>
          </div>
        </div>
      </div>

      <div class="settings-card" :class="{ disabled: !mainToggleEnabled }">
        <div class="setting-item">
          <div class="setting-info">
            <div class="setting-title">
              <span class="setting-icon mdi mdi-doctor"></span>
              Doctors
            </div>
            <div class="setting-description">
              Send a WhatsApp notification to the assigned doctor when a report
              is ready.
            </div>
          </div>
          <div class="setting-control">
            <div class="toggle-switch">
              <input
                type="checkbox"
                v-model="messageSettings.sendToDoctor"
                @change="
                  handleSettingChange('sendToDoctor', $event.target.checked)
                "
                :disabled="loading"
                class="toggle-input"
                id="sendToDoctor"
              />
              <label for="sendToDoctor" class="toggle-slider"></label>
            </div>
            <span
              class="setting-status"
              :class="{
                enabled: messageSettings.sendToDoctor,
                disabled: !messageSettings.sendToDoctor
              }"
            >
              {{ messageSettings.sendToDoctor ? "Enabled" : "Disabled" }}
            </span>
          </div>
        </div>

        <div class="setting-item">
          <div class="setting-info">
            <div class="setting-title">
              <span class="setting-icon mdi mdi-desk"></span>
              Reception
            </div>
            <div class="setting-description">
              Send report delivery updates to reception staff.
            </div>
          </div>
          <div class="setting-control">
            <div class="toggle-switch">
              <input
                type="checkbox"
                v-model="messageSettings.receptionMessage"
                @change="
                  handleSettingChange('receptionMessage', $event.target.checked)
                "
                :disabled="loading"
                class="toggle-input"
                id="receptionMessage"
              />
              <label for="receptionMessage" class="toggle-slider"></label>
            </div>
            <span
              class="setting-status"
              :class="{
                enabled: messageSettings.receptionMessage,
                disabled: !messageSettings.receptionMessage
              }"
            >
              {{ messageSettings.receptionMessage ? "Enabled" : "Disabled" }}
            </span>
          </div>
        </div>
      </div>

      <div class="settings-actions">
        <Button
          :onClick="saveAllSettings"
          className="button--primary"
          :disabled="loading || !hasChanges"
          title="Save"
        />
        <Button
          :onClick="resetSettings"
          className="button--secondary"
          :disabled="loading || !hasChanges"
          title="Reset"
        />
      </div>

      <div v-if="loading" class="loading-overlay">
        <div class="loading-spinner"></div>
        <span>Saving changes...</span>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "@/components/Button";
import { whatsappApi } from "../services/whatsappApi";

export default {
  name: "WhatsAppMessageSettings",
  components: {
    Button
  },
  data() {
    return {
      loading: false,
      mainToggleEnabled: false,
      messageSettings: {
        sendToDoctor: false,
        receptionMessage: false
      },
      originalSettings: {
        sendToDoctor: false,
        receptionMessage: false
      }
    };
  },
  computed: {
    hasChanges() {
      return (
        this.messageSettings.sendToDoctor !==
          this.originalSettings.sendToDoctor ||
        this.messageSettings.receptionMessage !==
          this.originalSettings.receptionMessage
      );
    }
  },
  methods: {
    async handleMainToggleChange() {
      try {
        this.loading = true;
        const response = await whatsappApi.toggleMessaging(
          this.mainToggleEnabled
        );

        if (response.result) {
          this.$toasted
            .success(
              `WhatsApp messaging service ${
                this.mainToggleEnabled ? "enabled" : "disabled"
              } successfully`
            )
            .goAway(3000);
        }
      } catch (error) {
        console.error("Error toggling messaging service:", error);
        // Revert the toggle state on error
        this.mainToggleEnabled = !this.mainToggleEnabled;
        this.$toasted
          .error("Failed to toggle messaging service. Please try again.")
          .goAway(3000);
      } finally {
        this.loading = false;
      }
    },

    async handleSettingChange(setting, value) {
      this.messageSettings[setting] = value;

      // Auto-save individual setting
      try {
        this.loading = true;
        await whatsappApi.updateMessageSettings({
          messageSettings: {
            sendToDoctor: this.messageSettings.sendToDoctor,
            receptionMessage: this.messageSettings.receptionMessage
          }
        });

        this.originalSettings[setting] = value;
        this.$toasted
          .success(
            `${
              setting === "sendToDoctor"
                ? "Doctor messages"
                : "Reception messages"
            } ${value ? "enabled" : "disabled"} successfully`
          )
          .goAway(3000);
      } catch (error) {
        console.error(`Error updating ${setting}:`, error);
        // Revert the change on error
        this.messageSettings[setting] = !value;
        this.$toasted
          .error(
            `Failed to update ${
              setting === "sendToDoctor"
                ? "doctor messages"
                : "reception messages"
            } setting. Please try again.`
          )
          .goAway(3000);
      } finally {
        this.loading = false;
      }
    },

    async saveAllSettings() {
      try {
        this.loading = true;
        await whatsappApi.updateMessageSettings({
          messageSettings: this.messageSettings
        });

        this.originalSettings = { ...this.messageSettings };
        this.$toasted
          .success("All message settings saved successfully")
          .goAway(3000);
      } catch (error) {
        console.error("Error saving message settings:", error);
        this.$toasted
          .error("Failed to save message settings. Please try again.")
          .goAway(3000);
      } finally {
        this.loading = false;
      }
    },

    resetSettings() {
      this.messageSettings = { ...this.originalSettings };
    },

    async fetchMessageSettings() {
      try {
        this.loading = true;

        // Fetch both main toggle status and message settings
        const [
          messagingStatusResponse,
          messageSettingsResponse
        ] = await Promise.all([
          whatsappApi.getMessagingStatus(),
          whatsappApi.getMessageSettings()
        ]);

        // Set main toggle status
        if (messagingStatusResponse.result) {
          this.mainToggleEnabled =
            messagingStatusResponse.result.enableWhatsAppMessaging || false;
        }

        // Set message settings
        if (
          messageSettingsResponse.result &&
          messageSettingsResponse.result.messageSettings
        ) {
          this.messageSettings = {
            sendToDoctor:
              messageSettingsResponse.result.messageSettings.sendToDoctor ||
              false,
            receptionMessage:
              messageSettingsResponse.result.messageSettings.receptionMessage ||
              false
          };
          this.originalSettings = { ...this.messageSettings };
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        this.$toasted
          .error("Failed to load settings. Using default values.")
          .goAway(3000);
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.fetchMessageSettings();
  }
};
</script>

<style scoped>
.message-settings-container {
  min-height: 100%;
  padding: 0;
}

.settings-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 16px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
}

.header-icon {
  width: 36px;
  height: 36px;
  background: var(--accent-050);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent-600);
  font-size: 20px;
}

.header-content h3 {
  margin: 0 0 2px;
  font-size: var(--fs-h2);
  font-weight: 700;
  color: var(--ink-900);
  letter-spacing: 0;
}

.header-content p {
  margin: 0;
  color: var(--muted);
  font-size: var(--fs-sm);
}

.settings-content {
  position: relative;
}

.main-toggle-section {
  margin-bottom: 14px;
}

.main-toggle-card {
  background: var(--surface);
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text);
}

.main-toggle-info {
  flex: 1;
  margin-right: 16px;
}

.main-toggle-title {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 4px;
  color: var(--ink-900);
  font-size: var(--fs-body);
  font-weight: 700;
}

.main-toggle-icon {
  color: var(--accent-600);
  font-size: 19px;
}

.main-toggle-description {
  font-size: var(--fs-sm);
  color: var(--muted);
  line-height: 1.5;
}

.main-toggle-control {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.main-toggle-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
}

.main-toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.main-toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--line-strong);
  transition: var(--dur) var(--ease);
  border-radius: 30px;
  border: 0;
}

.main-toggle-slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: #fff;
  transition: var(--dur) var(--ease);
  border-radius: 50%;
  box-shadow: var(--shadow-sm);
}

.main-toggle-input:checked + .main-toggle-slider {
  background-color: var(--accent-600);
}

.main-toggle-input:checked + .main-toggle-slider:before {
  transform: translateX(22px);
}

.main-toggle-input:disabled + .main-toggle-slider {
  opacity: 0.6;
  cursor: not-allowed;
}

.main-toggle-status {
  font-size: var(--fs-xs);
  font-weight: 700;
  padding: 4px 9px;
  border-radius: var(--radius-sm);
  letter-spacing: 0;
  min-width: 72px;
  text-align: center;
  background-color: var(--surface-2);
  color: var(--muted);
}

.main-toggle-status.enabled {
  background-color: rgba(31, 122, 82, 0.1);
  color: var(--success);
}

.main-toggle-status.disabled {
  color: var(--muted);
}

.settings-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  overflow: hidden;
  margin-bottom: 16px;
  transition: opacity var(--dur) var(--ease);
}

.settings-card.disabled {
  opacity: 0.5;
  pointer-events: none;
  background: var(--surface-2);
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid var(--line);
}

.setting-item:last-child {
  border-bottom: none;
}

.setting-info {
  flex: 1;
  margin-right: 16px;
}

.setting-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--fs-body);
  font-weight: 700;
  color: var(--ink-900);
  margin-bottom: 3px;
}

.setting-icon {
  color: var(--accent-600);
  font-size: 18px;
}

.setting-description {
  color: var(--muted);
  font-size: var(--fs-sm);
  line-height: 1.5;
}

.setting-control {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
}

.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--line-strong);
  transition: var(--dur) var(--ease);
  border-radius: 30px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: #fff;
  transition: var(--dur) var(--ease);
  border-radius: 50%;
  box-shadow: var(--shadow-sm);
}

.toggle-input:checked + .toggle-slider {
  background-color: var(--accent-600);
}

.toggle-input:checked + .toggle-slider:before {
  transform: translateX(22px);
}

.toggle-input:disabled + .toggle-slider {
  opacity: 0.6;
  cursor: not-allowed;
}

.setting-status {
  font-size: var(--fs-xs);
  font-weight: 700;
  padding: 4px 10px;
  border-radius: var(--radius-sm);
  min-width: 80px;
  text-align: center;
}

.setting-status.enabled {
  background-color: rgba(31, 122, 82, 0.1);
  color: var(--success);
}

.setting-status.disabled {
  background-color: var(--surface-2);
  color: var(--muted);
}

.settings-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  border-radius: var(--radius-md);
  z-index: 10;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--line);
  border-top: 3px solid var(--accent-600);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Responsive design */
@media (max-width: 768px) {
  .setting-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .setting-info {
    margin-right: 0;
  }

  .setting-control {
    width: 100%;
    justify-content: space-between;
  }

  .settings-actions {
    flex-direction: column;
  }
}
</style>
